/*
 * PE Product Intake interceptor (Sprint 3 — Path B trigger)
 * ---------------------------------------------------------
 * Hijacks the buy button on products that require MDI intake (flagged in Liquid
 * via data-pe-requires-intake on <product-form> / the submit button) so that,
 * instead of adding to cart, the click launches the product-specific intake
 * flow and (later) checks out the selected variant.
 *
 * Design constraints (see sprints/Sprint3/sprint-3-specs.md):
 *   - Do NOT edit assets/product-form.js — it is shared by quick-add and other
 *     surfaces. This file attaches a GLOBAL, CAPTURE-PHASE listener on document
 *     and calls preventDefault() + stopImmediatePropagation() so it pre-empts
 *     the Sense <product-form> submit handler (bound bubble-phase on the inner
 *     <form>) and any page-builder handler.
 *   - Selector-based + global so it fires across ALL product template types:
 *     native main-product (<product-form>) and page-builder templates
 *     (PageFly / Pagetify / Gem / Shogun) whose ATC markup is rendered
 *     client-side and is not in the theme repo.
 *   - Capture the selected variant from the form's hidden [name="id"] input and
 *     stash it in sessionStorage['pe_selected_variant'] (the same key the intake
 *     section reads, see sections/pe-intake-form-content.liquid).
 *   - Debounce to one trigger per click (mirrors the section's checkoutStarted
 *     guard) so we never spawn orphan vouchers later.
 *
 * Branch status:
 *   - Logged-in branch (Phase 4 — DONE): POSTs /apps/pe-mdi/start-intake
 *     { product_id, variant_id }; the app creates the product-specific voucher,
 *     writes custom.onboarding_url, and returns it. On success we navigate to the
 *     intake page carrying ?variant=<id> so the intake section renders this
 *     voucher and checks out the SELECTED variant.
 *   - Logged-out branch redirects to /account/register?pe_product_id=&pe_variant=
 *     (consumed by main-register.liquid in Phase 2).
 */
(function () {
  'use strict';

  var VARIANT_KEY = 'pe_selected_variant';
  // Sprint 15: the customer-selected Loop selling plan (subscription cadence). The
  // intake flow bypasses the native /cart/add (which would carry `selling_plan`),
  // so we capture the plan here and thread it through the SAME sessionStorage
  // carrier as the variant, so the intake page's cart-add can re-attach it and the
  // order checks out as a subscription instead of collapsing to one-time.
  var SELLING_PLAN_KEY = 'pe_selected_selling_plan';
  // Sprint 16: the BASE product's handle, carried alongside the plan so the intake
  // page can look up the base plan's CADENCE and match it against the ENHANCED
  // product's own plans when an upsell swap is accepted (carry over the subscription
  // frequency instead of collapsing to one-time). Only meaningful when a plan is
  // selected; cleared otherwise so it can't leak onto a later one-time click.
  var PRODUCT_HANDLE_KEY = 'pe_selected_product_handle';
  // Debounce window: one intake trigger per click burst. Mirrors the intake
  // section's `checkoutStarted` single-fire guard so a double-click / re-render
  // can't kick off two vouchers.
  var TRIGGER_COOLDOWN_MS = 4000;
  var lastTriggerAt = 0;
  var triggerInFlight = false;

  function normalizeId(value) {
    if (!value) return null;
    var s = String(value).trim();
    if (!/^\d+$/.test(s)) return null;
    var id = Number(s);
    if (!Number.isFinite(id) || id <= 0) return null;
    return String(id);
  }

  // Locale-aware root. window.routes (Sense) does NOT carry root_url, so
  // main-product.liquid injects window.__pe_root_url = {{ routes.root_url }}.
  // Fall back to "/" for the default locale if the global is absent.
  function rootUrl() {
    var root = window.__pe_root_url || (window.routes && window.routes.root_url) || '/';
    if (!String(root).endsWith('/')) root += '/';
    return root;
  }

  // Resolve the app-proxy root (e.g. "/apps/pe-mdi/").
  function appProxyUrl(path) {
    return rootUrl() + 'apps/pe-mdi/' + String(path || '').replace(/^\//, '');
  }

  function registerUrl() {
    return rootUrl() + 'account/register';
  }

  // The intake page that hosts the MDI iframe (sections/pe-intake-form-content).
  // After start-intake writes custom.onboarding_url, navigating here renders the
  // freshly created product-specific voucher. Matches the register return_to
  // (pages['pe-intake-form']) so logged-in and logged-out paths converge.
  function intakeUrl() {
    return rootUrl() + 'pages/pe-intake-form';
  }

  // True once per click burst; false (and logs) if we're still cooling down.
  function claimTrigger() {
    var now = Date.now();
    if (triggerInFlight || (now - lastTriggerAt) < TRIGGER_COOLDOWN_MS) {
      console.debug('[PE intake] trigger debounced (one intake per click)');
      return false;
    }
    lastTriggerAt = now;
    triggerInFlight = true;
    return true;
  }

  function releaseTrigger() {
    triggerInFlight = false;
  }

  // Find the intake-flagged buy surface for a given event target. Returns
  // { productId, form } or null if this surface does not require intake.
  // Covers: native <product-form>[data-pe-requires-intake]; the flagged submit
  // button; and any builder form posting to /cart/add that wraps a flagged node.
  function matchIntakeSurface(target) {
    if (!target || typeof target.closest !== 'function') return null;

    // 1) Native Sense surface or the flagged submit button.
    var flagged =
      target.closest('product-form[data-pe-requires-intake]') ||
      target.closest('[data-pe-requires-intake]');

    // 2) Page-builder ATC: a cart/add form (or a button inside one). We can only
    //    treat it as an intake surface if it is flagged (builders that reuse the
    //    Sense button keep the data attribute) OR it wraps a flagged node.
    var form =
      target.closest('product-form[data-pe-requires-intake] form') ||
      target.closest('form[action*="/cart/add"]') ||
      (flagged && flagged.querySelector && flagged.querySelector('form[action*="/cart/add"]'));

    if (!flagged && !form) return null;

    // If we matched a generic cart/add form with no flag anywhere, bail — that
    // is a non-intake product (e.g. a builder upsell) and must add to cart normally.
    var hasFlag =
      flagged ||
      (form && (form.closest('[data-pe-requires-intake]') ||
                form.querySelector('[data-pe-requires-intake]')));
    if (!hasFlag) return null;

    var productId =
      (flagged && normalizeId(flagged.getAttribute('data-pe-product-id'))) ||
      (form && form.closest('[data-pe-product-id]') &&
        normalizeId(form.closest('[data-pe-product-id]').getAttribute('data-pe-product-id'))) ||
      null;

    return { productId: productId, form: form || (flagged && flagged.querySelector('form')) || null };
  }

  // Read the selected variant id out of the buy form's hidden [name="id"] input.
  // Sense disables it in markup and re-enables it in the product-form constructor,
  // so read by value regardless of the disabled flag.
  function readSelectedVariant(form) {
    if (!form) return null;
    var input = form.querySelector('[name="id"]');
    if (input) {
      var v = normalizeId(input.value);
      if (v) return v;
    }
    // Builder fallback: some builders carry the variant in a data attribute.
    var alt = form.querySelector('[data-variant-id]');
    if (alt) return normalizeId(alt.getAttribute('data-variant-id'));
    return null;
  }

  // Sprint 15: read the customer-selected selling plan id (subscription cadence).
  // Search a given scope for the native `selling_plan` control: a checked radio,
  // a <select>, or a hidden input (the value is the numeric selling plan id; a
  // one-time selection is empty/absent -> null). Also supports a data-attribute
  // fallback some widgets use.
  function findSellingPlanValue(scope) {
    if (!scope || typeof scope.querySelector !== 'function') return null;
    var el =
      scope.querySelector('[name="selling_plan"]:checked') ||
      scope.querySelector('select[name="selling_plan"]') ||
      scope.querySelector('input[name="selling_plan"]');
    if (el) {
      var v = normalizeId(el.value);
      if (v) return v;
    }
    var alt = scope.querySelector('[data-selling-plan-id], [data-selling-plan]');
    if (alt) {
      var d = normalizeId(
        alt.getAttribute('data-selling-plan-id') || alt.getAttribute('data-selling-plan'),
      );
      if (d) return d;
    }
    return null;
  }

  // Loop's app-block selling-plan widget may inject the `selling_plan` control
  // INSIDE the product form (native pattern) OR render it as a sibling block within
  // the same product section. Search the form first, then widen to the enclosing
  // section, then (single-product pages, the PE norm) the document. Returns the
  // numeric plan id or null (null == one-time, so no plan is attached downstream).
  function readSelectedSellingPlan(form) {
    return (
      findSellingPlanValue(form) ||
      findSellingPlanValue(form && form.closest && form.closest('.shopify-section')) ||
      findSellingPlanValue(document)
    );
  }

  // Sprint 16: the current product's handle, threaded to the intake page so it can
  // read the BASE plan's cadence when carrying a subscription onto an accepted upsell
  // swap. Product detail pages are the native "Default product" template, so the path
  // is /products/<handle> (optionally locale-prefixed) -- read it from there first,
  // then fall back to a form data-attribute some builders expose. Returns null if
  // no handle can be determined (the intake page then degrades to one-time on a swap).
  function readProductHandle(form) {
    try {
      var m = String(location.pathname || '').match(/\/products\/([^\/?#]+)/);
      if (m && m[1]) return decodeURIComponent(m[1]);
    } catch (e) {}
    try {
      var node = form && form.closest && form.closest('[data-product-handle]');
      if (node) {
        var h = (node.getAttribute('data-product-handle') || '').trim();
        if (h) return h;
      }
    } catch (e) {}
    return null;
  }

  function customerId() {
    // Rendered from Liquid {{ customer.id }} (see main-product.liquid). Treat any
    // truthy numeric value as logged in.
    return normalizeId(window.__pe_customer_id);
  }

  // The actual intake launch. Returns a promise so we can release the debounce.
  function launchIntake(productId, variantId, sellingPlanId, productHandle) {
    if (variantId) {
      try {
        sessionStorage.setItem(VARIANT_KEY, variantId);
        console.debug('[PE intake] stored selected variant', variantId);
      } catch (e) {
        console.warn('[PE intake] could not write sessionStorage', e);
      }
    }

    // Sprint 15: carry the selected selling plan through the SAME sessionStorage
    // carrier as the variant so it survives the register/login redirect (logged-out)
    // and the intake-page navigation (logged-in). Store when a plan is selected;
    // CLEAR otherwise so a stale plan from a previous product click never leaks onto
    // a one-time product.
    try {
      if (sellingPlanId) {
        sessionStorage.setItem(SELLING_PLAN_KEY, sellingPlanId);
        console.debug('[PE intake] stored selected selling plan', sellingPlanId);
      } else {
        sessionStorage.removeItem(SELLING_PLAN_KEY);
      }
    } catch (e) {
      console.warn('[PE intake] could not write selling plan to sessionStorage', e);
    }

    // Sprint 16 / Sprint 17d: carry the base product handle for the intake page. It
    // is needed to resolve the cadence for an accepted upsell swap (Sprint 16) AND to
    // read the BASE product's per-delivery price so the upsell modal can show the TRUE
    // incremental upgrade cost (enhanced - base) -- required on EVERY path, including
    // one-time, so it is now stored whenever a handle is known (not gated on a plan).
    // Cleared when no handle so a stale handle can't leak onto a later click. The
    // sessionStorage carrier survives the logged-out register->login->intake journey,
    // mirroring the selling-plan carrier.
    try {
      if (productHandle) {
        sessionStorage.setItem(PRODUCT_HANDLE_KEY, productHandle);
        console.debug('[PE intake] stored base product handle', productHandle);
      } else {
        sessionStorage.removeItem(PRODUCT_HANDLE_KEY);
      }
    } catch (e) {
      console.warn('[PE intake] could not write product handle to sessionStorage', e);
    }

    var loggedInId = customerId();

    if (loggedInId) {
      // ---- Logged-in branch (Phase 4) ----
      // Navigate straight to the intake page with the SELECTED product on the URL.
      // The intake page POSTs /apps/pe-mdi/start-intake itself and loads the
      // onboarding_url from that response.
      //
      // This used to fire start-intake from here with `keepalive: true` (Sprint 27)
      // and let the intake page poll custom.onboarding_url. For a returning customer
      // that metafield still held the PREVIOUS voucher's URL when the intake page
      // rendered, so the page loaded the old, already-used voucher and never polled
      // or provisioned: the customer sat on the loading video. Requesting from the
      // intake page also avoids two concurrent start-intake calls minting two
      // vouchers. The loading video covers the wait there.
      // (sellingPlanId is still carried on the intake URL below.)

      var dest = new URL(intakeUrl(), window.location.origin);
      // The variant read off the buy button. We no longer have the app's echoed
      // variant to prefer (nothing is awaited), but the intake page resolves one
      // from ?product= via the mapping proxy when this is absent, so an unresolved
      // variant is not a dead end.
      if (variantId) dest.searchParams.set('variant', variantId);
      // Carry the SELECTED product id explicitly. The intake page otherwise reads
      // it from the customer's custom.selected_product_id metafield, which start-intake
      // writes server-side but the storefront may not reflect on THIS immediate render
      // (read-after-write lag) -- and now that we do not wait for the POST at all, that
      // metafield is even less likely to be readable in time. When empty, the intake
      // page silently falls back to the gender-DEFAULT product, so the upsell + MDI
      // case-offering swap resolve a DIFFERENT product than the case was built from
      // (wrong enhanced offering, and the original base left attached). The URL param
      // is the authoritative, race-free source of the product the customer chose.
      if (productId) dest.searchParams.set('product', productId);
      // Sprint 15: carry the selected selling plan on the intake URL too (belt-and
      // -suspenders alongside the sessionStorage carrier) so the intake cart-add
      // can re-attach it and the order checks out as a subscription.
      if (sellingPlanId) dest.searchParams.set('selling_plan', sellingPlanId);
      // Sprint 16 / Sprint 17d: carry the base handle so the intake page can read
      // the base plan's cadence for an accepted upsell swap AND the base product's
      // per-delivery price for the modal's true upgrade delta (belt-and-suspenders
      // alongside the sessionStorage carrier). Sent on EVERY path (incl. one-time)
      // so the delta is computable regardless of cadence.
      if (productHandle) dest.searchParams.set('product_handle', productHandle);
      console.debug('[PE intake] provisioning in background — navigating to intake now', dest.toString());
      window.location.assign(dest.toString());
      return Promise.resolve();
    }

    // ---- Logged-out branch ----
    // Redirect to registration carrying the selected product/variant. The register
    // form will consume these params in Phase 2 (not yet). Build the URL per spec.
    var url = new URL(registerUrl(), window.location.origin);
    if (productId) url.searchParams.set('pe_product_id', productId);
    if (variantId) url.searchParams.set('pe_variant', variantId);
    // Sprint 15: carry the selected selling plan on the register redirect as a query
    // param (the sessionStorage carrier set above is the primary vehicle; this is
    // belt-and-suspenders). The intake page re-captures ?pe_selling_plan on arrival.
    if (sellingPlanId) url.searchParams.set('pe_selling_plan', sellingPlanId);
    // Sprint 16 / Sprint 17d: carry the base handle on the register redirect too (the
    // sessionStorage carrier set above is the primary vehicle across the journey).
    // Sent on every path (incl. one-time) so the modal's upgrade delta is computable.
    if (productHandle) url.searchParams.set('pe_product_handle', productHandle);
    console.debug('[PE intake] logged out — redirecting to register', url.toString());
    window.location.assign(url.toString());
    return Promise.resolve();
  }

  function handleIntake(event, target) {
    var surface = matchIntakeSurface(target);
    if (!surface) return false;

    // Pre-empt Sense's <product-form> submit handler and any builder handler.
    event.preventDefault();
    event.stopImmediatePropagation();

    if (!claimTrigger()) return true;

    var variantId = readSelectedVariant(surface.form);
    var sellingPlanId = readSelectedSellingPlan(surface.form);
    var productHandle = readProductHandle(surface.form);
    console.debug('[PE intake] intercepted buy', {
      productId: surface.productId,
      variantId: variantId,
      sellingPlanId: sellingPlanId,
      productHandle: productHandle
    });

    Promise.resolve()
      .then(function () { return launchIntake(surface.productId, variantId, sellingPlanId, productHandle); })
      .catch(function (err) { console.error('[PE intake] launch failed', err); })
      .finally(function () {
        // For the logged-in placeholder path we release immediately so testing
        // can retry; real navigation (logged-out) leaves the page anyway.
        releaseTrigger();
      });

    return true;
  }

  // Capture-phase submit: native <product-form> and any builder cart/add form.
  document.addEventListener(
    'submit',
    function (event) {
      handleIntake(event, event.target);
    },
    true
  );

  // Capture-phase click: page-builder ATC buttons that add to cart via JS without
  // a real form submit. Only fires for flagged surfaces (matchIntakeSurface gates).
  document.addEventListener(
    'click',
    function (event) {
      var btn = event.target && event.target.closest
        ? event.target.closest('button, a, [role="button"]')
        : null;
      if (!btn) return;
      // Skip native submit buttons inside a <form> — the submit listener above
      // handles those and we don't want to double-fire.
      if (btn.type === 'submit' && btn.closest('form')) {
        // Only intercept here if there is no enclosing form action (pure JS ATC).
        return;
      }
      handleIntake(event, btn);
    },
    true
  );

  console.debug('[PE intake] interceptor armed (capture phase)');
})();
